import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Rating,
  CircularProgress
} from '@mui/material';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import StorefrontIcon from '@mui/icons-material/Storefront';
import StarIcon from '@mui/icons-material/Star';
import AddIcon from '@mui/icons-material/Add';
import { getRestaurants } from '../services/restaurantService';

function Dashboard() {
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalRestaurants: 0,
    featuredRestaurants: 0,
    averageRating: 0,
    bbqStyles: {}
  });

  useEffect(() => {
    fetchRestaurants();
  }, []);

  const fetchRestaurants = async () => {
    try {
      setLoading(true);
      const data = await getRestaurants();
      setRestaurants(data);
      
      // Calculate dashboard stats
      calculateStats(data);
    } catch (error) {
      console.error('Error fetching restaurants:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (restaurants) => {
    const totalRestaurants = restaurants.length;
    const featuredRestaurants = restaurants.filter(r => r.isFeatured).length;
    
    // Calculate average rating
    const totalRating = restaurants.reduce((sum, r) => sum + (r.rating || 0), 0);
    const averageRating = totalRestaurants > 0 ? totalRating / totalRestaurants : 0;
    
    // Count BBQ styles
    const bbqStyles = restaurants.reduce((styles, restaurant) => {
      if (restaurant.bbqStyle) {
        styles[restaurant.bbqStyle] = (styles[restaurant.bbqStyle] || 0) + 1;
      }
      return styles;
    }, {});
    
    setStats({
      totalRestaurants,
      featuredRestaurants,
      averageRating,
      bbqStyles
    });
  };

  // Get recently added restaurants
  const recentRestaurants = [...restaurants]
    .sort((a, b) => {
      const dateA = a.created_at ? new Date(a.created_at.seconds * 1000) : new Date(0);
      const dateB = b.created_at ? new Date(b.created_at.seconds * 1000) : new Date(0);
      return dateB - dateA;
    })
    .slice(0, 3);

  // Get featured restaurants
  const featuredRestaurants = restaurants.filter(r => r.isFeatured).slice(0, 3);

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Dashboard
        </Typography>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Paper
              sx={{
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                height: 140,
                bgcolor: 'primary.main',
                color: 'white'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <RestaurantIcon sx={{ mr: 1 }} />
                <Typography variant="h6" component="div">
                  Total Restaurants
                </Typography>
              </Box>
              <Typography variant="h3" component="div" sx={{ mt: 'auto' }}>
                {stats.totalRestaurants}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Paper
              sx={{
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                height: 140,
                bgcolor: 'secondary.main',
                color: 'white'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <StarIcon sx={{ mr: 1 }} />
                <Typography variant="h6" component="div">
                  Featured Restaurants
                </Typography>
              </Box>
              <Typography variant="h3" component="div" sx={{ mt: 'auto' }}>
                {stats.featuredRestaurants}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Paper
              sx={{
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                height: 140,
                bgcolor: 'info.main',
                color: 'white'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <StorefrontIcon sx={{ mr: 1 }} />
                <Typography variant="h6" component="div">
                  Average Rating
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 'auto' }}>
                <Typography variant="h3" component="div">
                  {stats.averageRating.toFixed(1)}
                </Typography>
                <Rating 
                  value={stats.averageRating} 
                  precision={0.5} 
                  readOnly 
                  sx={{ ml: 1 }}
                />
              </Box>
            </Paper>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          {/* BBQ Styles */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                BBQ Styles
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ mb: 2 }}>
                {Object.keys(stats.bbqStyles).length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {Object.entries(stats.bbqStyles).map(([style, count]) => (
                      <Chip 
                        key={style}
                        label={`${style} (${count})`}
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No BBQ styles defined yet
                  </Typography>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Recent Restaurants */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Recently Added
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <List>
                {recentRestaurants.length > 0 ? (
                  recentRestaurants.map((restaurant) => (
                    <ListItem
                      key={restaurant.id}
                      component={RouterLink}
                      to={`/restaurants/edit/${restaurant.id}`}
                      sx={{ 
                        textDecoration: 'none',
                        color: 'inherit',
                        '&:hover': {
                          bgcolor: 'rgba(0, 0, 0, 0.04)'
                        }
                      }}
                    >
                      <ListItemAvatar>
                        {restaurant.logoPhotoURL ? (
                          <Avatar src={restaurant.logoPhotoURL} alt={restaurant.name} />
                        ) : (
                          <Avatar>
                            {restaurant.name.charAt(0).toUpperCase()}
                          </Avatar>
                        )}
                      </ListItemAvatar>
                      <ListItemText
                        primary={restaurant.name}
                        secondary={`${restaurant.city}, ${restaurant.state} • ${restaurant.bbqStyle || 'Unknown'} Style`}
                      />
                      <Rating value={restaurant.rating || 0} readOnly size="small" />
                    </ListItem>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No restaurants added yet
                  </Typography>
                )}
                
                {restaurants.length > 0 && (
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                    <Button
                      component={RouterLink}
                      to="/restaurants"
                      color="primary"
                    >
                      View All Restaurants
                    </Button>
                  </Box>
                )}
              </List>
            </Paper>
          </Grid>

          {/* Featured Restaurants */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Featured Restaurants
                </Typography>
                <Button
                  component={RouterLink}
                  to="/restaurants/add"
                  startIcon={<AddIcon />}
                  variant="contained"
                  color="primary"
                >
                  Add Restaurant
                </Button>
              </Box>
              <Divider sx={{ mb: 3 }} />
              
              <Grid container spacing={3}>
                {featuredRestaurants.length > 0 ? (
                  featuredRestaurants.map((restaurant) => (
                    <Grid item xs={12} sm={6} md={4} key={restaurant.id}>
                      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <CardMedia
                          component="img"
                          height="140"
                          image={restaurant.logoPhotoURL || restaurant.photoUrls?.[0] || 'https://via.placeholder.com/300x150?text=No+Image'}
                          alt={restaurant.name}
                        />
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Typography gutterBottom variant="h6" component="div">
                            {restaurant.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {restaurant.city}, {restaurant.state}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Rating value={restaurant.rating || 0} readOnly size="small" />
                            <Typography variant="body2" sx={{ ml: 1 }}>
                              {restaurant.rating?.toFixed(1) || 'No ratings'}
                            </Typography>
                          </Box>
                          <Chip 
                            label={restaurant.bbqStyle || 'Unknown Style'} 
                            size="small" 
                            color="primary"
                            variant="outlined"
                          />
                        </CardContent>
                        <CardActions>
                          <Button 
                            size="small" 
                            component={RouterLink}
                            to={`/restaurants/edit/${restaurant.id}`}
                          >
                            Edit
                          </Button>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))
                ) : (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" align="center">
                      No featured restaurants yet. Mark a restaurant as featured to see it here.
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
}

export default Dashboard;